import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { WhatsAppGroup, CreateGroupRequest, UpdateGroupRequest, UpdateSettingsRequest } from '../types/groupTypes';
import { groupsApi } from '../services/groupsApi';
import toast from 'react-hot-toast';

// ============================================================================
// CONTEXT TYPES
// ============================================================================

interface GroupsContextData {
  // Estado
  groups: WhatsAppGroup[];
  selectedGroup: WhatsAppGroup | null;
  loading: boolean;
  error: string | null;
  
  // Ações
  loadGroups: (instanceName?: string, forceRefresh?: boolean) => Promise<void>;
  selectGroup: (groupJid: string, forceRefresh?: boolean) => Promise<void>;
  createGroup: (data: CreateGroupRequest) => Promise<WhatsAppGroup>;
  updateGroup: (groupJid: string, data: UpdateGroupRequest) => Promise<void>;
  leaveGroup: (groupJid: string) => Promise<void>;
  syncGroup: (groupJid: string) => Promise<void>;
  
  // Participantes
  addParticipants: (groupJid: string, participants: string[]) => Promise<void>;
  removeParticipants: (groupJid: string, participants: string[]) => Promise<void>;
  promoteParticipants: (groupJid: string, participants: string[]) => Promise<void>;
  demoteParticipants: (groupJid: string, participants: string[]) => Promise<void>;
  
  // Convites
  getInviteCode: (groupJid: string) => Promise<{ inviteCode: string; inviteUrl: string }>;
  revokeInviteCode: (groupJid: string) => Promise<void>;
  sendInvites: (groupJid: string, numbers: string[], description?: string) => Promise<void>;
  
  // Configurações
  updateSettings: (groupJid: string, settings: UpdateSettingsRequest) => Promise<void>;
  
  // Utilidades
  clearError: () => void;
  clearSelectedGroup: () => void;
}

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const GroupsContext = createContext<GroupsContextData | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface GroupsProviderProps {
  children: ReactNode;
}

export const GroupsProvider: React.FC<GroupsProviderProps> = ({ children }) => {
  const [groups, setGroups] = useState<WhatsAppGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<WhatsAppGroup | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ==================== LOAD GROUPS ====================

  const loadGroups = useCallback(async (instanceName?: string, forceRefresh: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('📋 Carregando grupos...', { instanceName, forceRefresh });

      const data = await groupsApi.fetchGroups(instanceName, forceRefresh);
      setGroups(data);

      // console.log(`✅ ${data.length} grupos carregados`);
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao carregar grupos';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('❌ Erro ao carregar grupos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ==================== SELECT GROUP ====================

  const selectGroup = useCallback(async (groupJid: string, forceRefresh: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('🔍 Selecionando grupo:', groupJid);

      const data = await groupsApi.fetchGroupDetails(groupJid, forceRefresh);
      setSelectedGroup(data);

      // Atualizar na lista também
      setGroups(prev => prev.map(g => g.groupJid === groupJid ? data : g));

      // console.log('✅ Grupo selecionado:', data.subject);
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao buscar detalhes do grupo';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('❌ Erro ao selecionar grupo:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ==================== CREATE GROUP ====================

  const createGroup = useCallback(async (data: CreateGroupRequest): Promise<WhatsAppGroup> => {
    try {
      setLoading(true);
      setError(null);

      // console.log('✨ Criando grupo:', data.subject);

      const newGroup = await groupsApi.createGroup(data);
      
      // Adicionar à lista
      setGroups(prev => [newGroup, ...prev]);

      toast.success(`Grupo "${newGroup.subject}" criado com sucesso! 🎉`);
      // console.log('✅ Grupo criado:', newGroup.groupJid);

      return newGroup;
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao criar grupo';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('❌ Erro ao criar grupo:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ==================== UPDATE GROUP ====================

  const updateGroup = useCallback(async (groupJid: string, data: UpdateGroupRequest) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('📝 Atualizando grupo:', groupJid);

      const updatedGroup = await groupsApi.updateGroup(groupJid, data);

      // Atualizar na lista
      setGroups(prev => prev.map(g => g.groupJid === groupJid ? updatedGroup : g));

      // Atualizar selecionado se for o mesmo
      if (selectedGroup?.groupJid === groupJid) {
        setSelectedGroup(updatedGroup);
      }

      toast.success('Grupo atualizado com sucesso! ✅');
      // console.log('✅ Grupo atualizado');
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao atualizar grupo';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('❌ Erro ao atualizar grupo:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedGroup]);

  // ==================== LEAVE GROUP ====================

  const leaveGroup = useCallback(async (groupJid: string) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('👋 Saindo do grupo:', groupJid);

      await groupsApi.leaveGroup(groupJid);

      // Remover da lista
      setGroups(prev => prev.filter(g => g.groupJid !== groupJid));

      // Limpar selecionado se for o mesmo
      if (selectedGroup?.groupJid === groupJid) {
        setSelectedGroup(null);
      }

      toast.success('Você saiu do grupo com sucesso! 👋');
      // console.log('✅ Saída do grupo realizada');
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao sair do grupo';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('❌ Erro ao sair do grupo:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedGroup]);

  // ==================== SYNC GROUP ====================

  const syncGroup = useCallback(async (groupJid: string) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('🔄 Sincronizando grupo:', groupJid);

      const syncedGroup = await groupsApi.syncGroup(groupJid);

      // Atualizar na lista
      setGroups(prev => prev.map(g => g.groupJid === groupJid ? syncedGroup : g));

      // Atualizar selecionado se for o mesmo
      if (selectedGroup?.groupJid === groupJid) {
        setSelectedGroup(syncedGroup);
      }

      toast.success('Grupo sincronizado com sucesso! 🔄');
      // console.log('✅ Grupo sincronizado');
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao sincronizar grupo';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('❌ Erro ao sincronizar grupo:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedGroup]);

  // ==================== PARTICIPANTS ====================

  const addParticipants = useCallback(async (groupJid: string, participants: string[]) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('➕ Adicionando participantes:', participants.length);

      await groupsApi.addParticipants(groupJid, participants);

      // Recarregar grupo
      await selectGroup(groupJid, true);

      toast.success(`${participants.length} participante(s) adicionado(s)! ✅`);
      // console.log('✅ Participantes adicionados');
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao adicionar participantes';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('❌ Erro ao adicionar participantes:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectGroup]);

  const removeParticipants = useCallback(async (groupJid: string, participants: string[]) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('➖ Removendo participantes:', participants.length);

      await groupsApi.removeParticipants(groupJid, participants);

      // Recarregar grupo
      await selectGroup(groupJid, true);

      toast.success(`${participants.length} participante(s) removido(s)! ✅`);
      // console.log('✅ Participantes removidos');
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao remover participantes';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('❌ Erro ao remover participantes:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectGroup]);

  const promoteParticipants = useCallback(async (groupJid: string, participants: string[]) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('👑 Promovendo participantes:', participants.length);

      await groupsApi.promoteParticipants(groupJid, participants);

      // Recarregar grupo
      await selectGroup(groupJid, true);

      toast.success(`${participants.length} participante(s) promovido(s) a admin! 👑`);
      // console.log('✅ Participantes promovidos');
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao promover participantes';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('❌ Erro ao promover participantes:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectGroup]);

  const demoteParticipants = useCallback(async (groupJid: string, participants: string[]) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('⬇️ Rebaixando participantes:', participants.length);

      await groupsApi.demoteParticipants(groupJid, participants);

      // Recarregar grupo
      await selectGroup(groupJid, true);

      toast.success(`${participants.length} participante(s) rebaixado(s)! ✅`);
      // console.log('✅ Participantes rebaixados');
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao rebaixar participantes';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('❌ Erro ao rebaixar participantes:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectGroup]);

  // ==================== INVITES ====================

  const getInviteCode = useCallback(async (groupJid: string) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('🔗 Obtendo código de convite:', groupJid);

      const invite = await groupsApi.getInviteCode(groupJid);

      // console.log('✅ Código de convite obtido:', invite.inviteCode);
      return invite;
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao obter código de convite';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('❌ Erro ao obter código de convite:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const revokeInviteCode = useCallback(async (groupJid: string) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('🚫 Revogando código de convite:', groupJid);

      await groupsApi.revokeInviteCode(groupJid);

      toast.success('Código de convite revogado! Um novo código foi gerado. 🔄');
      // console.log('✅ Código de convite revogado');
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao revogar código de convite';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('❌ Erro ao revogar código de convite:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const sendInvites = useCallback(async (groupJid: string, numbers: string[], description?: string) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('📤 Enviando convites:', numbers.length);

      await groupsApi.sendInvites(groupJid, { numbers, description });

      toast.success(`Convites enviados para ${numbers.length} contato(s)! 📤`);
      // console.log('✅ Convites enviados');
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao enviar convites';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('❌ Erro ao enviar convites:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ==================== SETTINGS ====================

  const updateSettings = useCallback(async (groupJid: string, settings: UpdateSettingsRequest) => {
    try {
      setLoading(true);
      setError(null);

      // console.log('⚙️ Atualizando configurações:', groupJid);

      const updatedGroup = await groupsApi.updateSettings(groupJid, settings);

      // Atualizar na lista
      setGroups(prev => prev.map(g => g.groupJid === groupJid ? updatedGroup : g));

      // Atualizar selecionado se for o mesmo
      if (selectedGroup?.groupJid === groupJid) {
        setSelectedGroup(updatedGroup);
      }

      toast.success('Configurações atualizadas com sucesso! ⚙️');
      // console.log('✅ Configurações atualizadas');
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao atualizar configurações';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('❌ Erro ao atualizar configurações:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedGroup]);

  // ==================== UTILITIES ====================

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearSelectedGroup = useCallback(() => {
    setSelectedGroup(null);
  }, []);

  // ==================== CONTEXT VALUE ====================

  const value: GroupsContextData = {
    groups,
    selectedGroup,
    loading,
    error,
    loadGroups,
    selectGroup,
    createGroup,
    updateGroup,
    leaveGroup,
    syncGroup,
    addParticipants,
    removeParticipants,
    promoteParticipants,
    demoteParticipants,
    getInviteCode,
    revokeInviteCode,
    sendInvites,
    updateSettings,
    clearError,
    clearSelectedGroup
  };

  return (
    <GroupsContext.Provider value={value}>
      {children}
    </GroupsContext.Provider>
  );
};

// ============================================================================
// CUSTOM HOOK
// ============================================================================

export const useGroups = (): GroupsContextData => {
  const context = useContext(GroupsContext);
  
  if (context === undefined) {
    throw new Error('useGroups must be used within a GroupsProvider');
  }
  
  return context;
};

export default GroupsContext;

